import { useState } from 'react';
import { Calculator, TrendingUp, X, DollarSign, Info, Phone } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface ROICalculatorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ROICalculatorModal: React.FC<ROICalculatorModalProps> = ({ isOpen, onClose }) => {
  // Default only Virtual Host to show value; others start at 0 for clarity
  const [staffHours, setStaffHours] = useState(0);
  const [upsellItems, setUpsellItems] = useState(0);
  const [missedCalls, setMissedCalls] = useState(15);
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  // Calculate values (simplified - no plan selection)
  const staffSavings = staffHours * 4 * 20; // hours * weeks * $20/hr
  const upsellRevenue = upsellItems * 4 * 10; // items * weeks * $10/item
  const missedCallsRevenue = missedCalls * 4 * 25; // calls * weeks * $25 avg order

  const totalMonthlyValue = staffSavings + upsellRevenue + missedCallsRevenue;
  const annualValue = totalMonthlyValue * 12;

  return (
    <TooltipProvider>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="bg-[#1f4ed1] border-white/20 text-white max-w-3xl shadow-2xl shadow-black/50 [&>button]:hidden p-8">
          <DialogHeader className="pb-4">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-2xl font-bold flex items-center gap-3">
                <div className="p-2 bg-[#FDB913]/20 rounded-xl">
                  <Calculator className="w-6 h-6 text-[#FDB913]" />
                </div>
                Dine AI ROI Calculator
              </DialogTitle>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-white/70 hover:text-white" />
              </button>
            </div>
            <p className="text-white/70 text-base mt-2">
              See how much value Dine AI delivers as your 24/7 virtual assistant
            </p>
          </DialogHeader>
          
          <div className="space-y-5">
            {/* Value Sliders */}
            <div className="space-y-4">
              {/* Virtual Host - Missed Calls Slider (FEATURED) */}
              <div className="bg-gradient-to-r from-orange-500/20 to-amber-500/10 rounded-xl p-5 border border-orange-500/30">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-lg font-semibold text-white flex items-center gap-2">
                      <Phone className="w-5 h-5 text-orange-400" />
                      Virtual Host Recovers Missed Calls
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-4 h-4 text-white/40 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="bg-black/90 border-white/20 text-white text-sm max-w-xs">
                          AI answers every call 24/7. No more missed orders during rush hour or after hours. Average order $25.
                        </TooltipContent>
                      </Tooltip>
                    </h4>
                    <p className="text-sm text-white/50 mt-1">AI answers 24/7 @ $25/order avg</p>
                  </div>
                  <div className="text-right">
                    <span className="text-3xl font-bold text-orange-400">{missedCalls}</span>
                    <span className="text-sm text-white/60 ml-1">calls/week</span>
                  </div>
                </div>
                <input
                  type="range"
                  min="5"
                  max="50"
                  value={missedCalls}
                  onChange={(e) => setMissedCalls(Number(e.target.value))}
                  className="w-full h-2.5 bg-white/20 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:bg-orange-400 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg"
                  style={{
                    background: `linear-gradient(to right, #f97316 0%, #f97316 ${((missedCalls - 5) / 45) * 100}%, rgba(255,255,255,0.2) ${((missedCalls - 5) / 45) * 100}%, rgba(255,255,255,0.2) 100%)`
                  }}
                />
                <div className="flex justify-between items-center mt-2">
                  <span className="text-xs text-white/40">5 calls</span>
                  <span className="text-base text-orange-400 font-bold">
                    {formatCurrency(missedCallsRevenue)}/mo recovered
                  </span>
                  <span className="text-xs text-white/40">50 calls</span>
                </div>
              </div>

              {/* Staff Hours Slider */}
              <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-lg font-semibold text-white flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-[#FDB913]" />
                      Virtual Assistant Saves Staff Hours
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-4 h-4 text-white/40 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="bg-black/90 border-white/20 text-white text-sm max-w-xs">
                          Based on $20/hr average. Time saved on phone orders, questions, and reservations.
                        </TooltipContent>
                      </Tooltip>
                    </h4>
                    <p className="text-sm text-white/50 mt-1">During peak hours @ $20/hr</p>
                  </div>
                  <div className="text-right">
                    <span className="text-3xl font-bold text-[#FDB913]">{staffHours}</span>
                    <span className="text-sm text-white/60 ml-1">hrs/week</span>
                  </div>
                </div>
                <input
                  type="range"
                  min="0"
                  max="20"
                  value={staffHours}
                  onChange={(e) => setStaffHours(Number(e.target.value))}
                  className="w-full h-2.5 bg-white/20 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:bg-[#FDB913] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg"
                  style={{
                    background: `linear-gradient(to right, #FDB913 0%, #FDB913 ${(staffHours / 20) * 100}%, rgba(255,255,255,0.2) ${(staffHours / 20) * 100}%, rgba(255,255,255,0.2) 100%)`
                  }}
                />
                <div className="flex justify-between items-center mt-2">
                  <span className="text-xs text-white/40">0 hrs</span>
                  <span className="text-base text-[#FDB913] font-bold">
                    {formatCurrency(staffSavings)}/mo
                  </span>
                  <span className="text-xs text-white/40">20 hrs</span>
                </div>
              </div>

              {/* Upsell Items Slider */}
              <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-lg font-semibold text-white flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-[#FDB913]" />
                      AI Menu Assistant Drives Upsell/Cross-sell
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-4 h-4 text-white/40 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="bg-black/90 border-white/20 text-white text-sm max-w-xs">
                          AI suggests pairings and add-ons that increase average order by $10.
                        </TooltipContent>
                      </Tooltip>
                    </h4>
                    <p className="text-sm text-white/50 mt-1">Smart recommendations @ $10/item</p>
                  </div>
                  <div className="text-right">
                    <span className="text-3xl font-bold text-[#FDB913]">{upsellItems}</span>
                    <span className="text-sm text-white/60 ml-1">items/week</span>
                  </div>
                </div>
                <input
                  type="range"
                  min="0"
                  max="20"
                  value={upsellItems}
                  onChange={(e) => setUpsellItems(Number(e.target.value))}
                  className="w-full h-2.5 bg-white/20 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:bg-[#FDB913] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg"
                  style={{
                    background: `linear-gradient(to right, #FDB913 0%, #FDB913 ${(upsellItems / 20) * 100}%, rgba(255,255,255,0.2) ${(upsellItems / 20) * 100}%, rgba(255,255,255,0.2) 100%)`
                  }}
                />
                <div className="flex justify-between items-center mt-2">
                  <span className="text-xs text-white/40">0 items</span>
                  <span className="text-base text-[#FDB913] font-bold">
                    {formatCurrency(upsellRevenue)}/mo
                  </span>
                  <span className="text-xs text-white/40">20 items</span>
                </div>
              </div>
            </div>

            {/* Value Summary - Simplified */}
            <div className="bg-gradient-to-r from-green-500/20 to-green-500/10 rounded-xl p-5 border border-green-500/30">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-500/20 rounded-full">
                  <DollarSign className="w-8 h-8 text-green-400" />
                </div>
                <div className="flex-1">
                  <p className="text-white/70 text-sm mb-1">You're leaving on the table:</p>
                  <p className="text-white font-bold text-4xl">
                    <span className="text-green-400">{formatCurrency(totalMonthlyValue)}</span>
                    <span className="text-white/60 text-lg font-normal">/month</span>
                  </p>
                  <p className="text-white/60 text-sm mt-2">
                    That's <span className="text-green-400 font-semibold">{formatCurrency(annualValue)}/year</span> in recovered revenue
                  </p>
                </div>
              </div>
            </div>

            {/* Bottom message */}
            <p className="text-center text-white/60 text-sm">
              Dine AI plans start at just $49/month — <span className="text-green-400 font-semibold">pays for itself in days, not months</span>
            </p>

            {/* CTA Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => window.location.href = '/restaurant-menu-search'}
                className="flex-1 bg-[#FDB913] text-black font-bold py-4 px-6 rounded-xl hover:bg-[#FDB913]/90 transition-all shadow-lg text-lg"
              >
                Start Recovering Revenue
              </button>
              <button
                onClick={onClose}
                className="px-6 bg-white/10 text-white font-medium py-4 rounded-xl hover:bg-white/20 transition-all border border-white/20 text-lg"
              >
                Close
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};